import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, Plus, Flame, Check, Calendar } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const HABIT_ICONS = ['🏃', '💧', '📚', '🧘', '💪', '🥗', '😴', '📝', '🎯', '💊'];

export default function HabitsPage() {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [habits, setHabits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newHabit, setNewHabit] = useState({ name: '', icon: '🎯' });

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    fetchHabits();
  }, []);

  const fetchHabits = async () => {
    try {
      const res = await axios.get(`${API}/api/habits`, { headers: getAuthHeader() });
      setHabits(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const addHabit = async () => {
    if (!newHabit.name.trim()) return;
    try {
      const res = await axios.post(`${API}/api/habits`, newHabit, { headers: getAuthHeader() });
      setHabits([...habits, res.data]);
      setNewHabit({ name: '', icon: '🎯' });
      setShowAdd(false);
      toast.success('Vana tillagd');
    } catch (error) {
      toast.error('Kunde inte lägga till');
    }
  };

  const toggleHabit = async (habitId) => {
    try {
      const res = await axios.put(`${API}/api/habits/${habitId}/complete`, {}, { headers: getAuthHeader() });
      setHabits(habits.map(h => {
        if (h.id === habitId) {
          const completions = res.data.completed 
            ? [...(h.completions || []), today]
            : (h.completions || []).filter(d => d !== today);
          return { ...h, completions, streak: res.data.streak };
        }
        return h;
      }));
    } catch (error) {
      toast.error('Kunde inte uppdatera');
    }
  };

  const deleteHabit = async (habitId) => {
    try {
      await axios.delete(`${API}/api/habits/${habitId}`, { headers: getAuthHeader() });
      setHabits(habits.filter(h => h.id !== habitId));
      toast.success('Vana borttagen');
    } catch (error) {
      toast.error('Kunde inte ta bort');
    }
  };

  const isCompletedToday = (habit) => {
    return habit.completions?.includes(today);
  };

  const completedToday = habits.filter(isCompletedToday).length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="habits-page">
      <header className="border-b border-border/30 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/category/planning')} className="rounded-full w-9 h-9 p-0">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-lg font-semibold">Vanor</h1>
            <p className="text-xs text-muted-foreground">{completedToday} av {habits.length} idag</p>
          </div>
          <Button onClick={() => setShowAdd(true)} className="rounded-full" size="sm">
            <Plus className="w-4 h-4 mr-1" /> Ny
          </Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Today's date */}
        <div className="text-center">
          <p className="text-sm text-muted-foreground flex items-center justify-center gap-2">
            <Calendar className="w-4 h-4" />
            {new Date().toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>

        {/* Progress */}
        {habits.length > 0 && (
          <div className="bg-white rounded-2xl p-4 border border-border/30">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Dagens framsteg</span>
              <span className="text-sm text-muted-foreground">{completedToday}/{habits.length}</span>
            </div>
            <div className="h-3 bg-secondary rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all duration-500"
                style={{ width: `${habits.length ? (completedToday / habits.length) * 100 : 0}%` }}
              />
            </div>
          </div>
        )}

        {/* Habits list */}
        {loading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => <div key={i} className="h-20 bg-secondary/50 rounded-xl animate-pulse" />)}
          </div>
        ) : habits.length > 0 ? (
          <div className="space-y-3">
            {habits.map(habit => (
              <div 
                key={habit.id}
                className={`bg-white rounded-xl p-4 border transition-all ${
                  isCompletedToday(habit) ? 'border-emerald-200 bg-emerald-50/50' : 'border-border/30'
                }`}
              >
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => toggleHabit(habit.id)}
                    className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl transition-all ${
                      isCompletedToday(habit) ? 'bg-emerald-500 text-white' : 'bg-secondary'
                    }`}
                  >
                    {isCompletedToday(habit) ? <Check className="w-6 h-6" /> : habit.icon}
                  </button>
                  <div className="flex-1">
                    <p className={`font-medium ${isCompletedToday(habit) ? 'text-emerald-700' : ''}`}>
                      {habit.name}
                    </p>
                    {habit.streak > 0 && (
                      <p className="text-xs text-amber-600 flex items-center gap-1">
                        <Flame className="w-3 h-3" /> {habit.streak} dagar i rad
                      </p>
                    )}
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => deleteHabit(habit.id)}
                    className="text-muted-foreground"
                  >
                    ×
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Flame className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">Lägg till din första vana</p>
          </div>
        )}

        {/* Add Habit Modal */}
        {showAdd && (
          <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md space-y-4 animate-slide-up">
              <h3 className="font-heading text-lg font-semibold">Ny vana</h3>
              
              <Input
                placeholder="Vanans namn (t.ex. Drick vatten)"
                value={newHabit.name}
                onChange={(e) => setNewHabit({...newHabit, name: e.target.value})}
                className="rounded-xl"
              />
              
              <div>
                <label className="text-sm font-medium mb-2 block">Välj ikon</label>
                <div className="flex flex-wrap gap-2">
                  {HABIT_ICONS.map(icon => (
                    <button
                      key={icon}
                      onClick={() => setNewHabit({...newHabit, icon})}
                      className={`w-10 h-10 rounded-xl text-xl flex items-center justify-center transition-all ${
                        newHabit.icon === icon ? 'bg-primary text-white' : 'bg-secondary'
                      }`}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowAdd(false)} className="flex-1 rounded-full">
                  Avbryt
                </Button>
                <Button onClick={addHabit} className="flex-1 rounded-full">
                  Lägg till
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
